/**
 * FullStory instrumentation for the DocsBot chat widget.
 *
 * ## Why this file has to exist
 *
 * The widget renders inside a shadow root (`#docsbotai-root` is the host — see
 * `docsBotShadowRoot()` in ./DocsBot.js). At the shadow boundary the browser **retargets**
 * events: a listener on `document` is told the click happened on `#docsbotai-root`, never on
 * the launcher, the send button, or the link in an answer. So FullStory's autocapture records
 * one indistinguishable click for every possible widget interaction, funnel steps written
 * against inner selectors or button text match nothing, and typing in the chat input is
 * invisible entirely.
 *
 * Attaching the listeners to the **shadow root** fixes that: inside the boundary
 * `event.target` is the real element, and `composedPath()` reaches through any nested tree.
 * The events below are then what the team's funnels are built on, rather than autocapture.
 *
 * ## Events emitted
 *
 * | Name                      | When                                                        |
 * | ------------------------- | ----------------------------------------------------------- |
 * | `DocsBot Opened`          | The chat panel appears, from the launcher or the pill        |
 * | `DocsBot Message Typed`   | First keystroke in the chat input per open                   |
 * | `DocsBot Question Asked`  | A question is actually submitted                             |
 * | `DocsBot Link Clicked`    | Any link inside the widget is clicked                        |
 *
 * `DocsBot Message Typed` is the answer to the ticket's "interacted (wrote something)". It
 * hangs off a plain `input` event, so it does not depend on DocsBot's internal markup and
 * cannot quietly break when they ship a redesign.
 *
 * `DocsBot Question Asked` is emitted only from signals that cannot false-positive: a `submit`
 * event, Enter in a non-empty field, or a question our own pill sent. A broader
 * "click any button while the input has text" detector was considered and rejected — a false
 * positive silently corrupts the funnel, and Typed already covers the requirement.
 *
 * ## Privacy
 *
 * Text a visitor typed is **never** sent to FullStory, only its length. Questions seeded by
 * our own pill are our copy, not theirs, so those are sent verbatim. DocsBot's own chat logs
 * remain the place to read what people actually asked.
 */

import { trackFullStoryEvent } from './FullStory.js';
import { docsBotShadowRoot, watchDocsBotPanel, whenDocsBotReady, WATCH_TIMEOUT } from './DocsBot.js';

export const EVENT_OPENED = 'DocsBot Opened';
export const EVENT_TYPED = 'DocsBot Message Typed';
export const EVENT_ASKED = 'DocsBot Question Asked';
export const EVENT_LINK_CLICKED = 'DocsBot Link Clicked';

/**
 * How long a trigger noted by our own code stays valid. The pill awaits the widget's open
 * promise, so the panel can take a moment to appear after the click.
 */
const TRIGGER_TTL = 3000;
/** One submit can surface as both a keydown and a `submit` event; collapse them. */
const ASKED_DEDUPE_WINDOW = 500;
/** Seeded questions are our own copy, but there is no reason to ship an essay to FullStory. */
const MAX_SEEDED_LENGTH = 200;

let initialised = false;
let panelOpen = false;
let typedThisOpen = false;
let lastTrigger = null;
let lastTriggerAt = 0;
let lastAskedAt = 0;

/** Reset module state. Tests only — the browser gets a fresh module on every page load. */
export function resetDocsBotAnalytics() {
    initialised = false;
    panelOpen = false;
    typedThisOpen = false;
    lastTrigger = null;
    lastTriggerAt = 0;
    lastAskedAt = 0;
}

function currentPath() {
    return typeof window === 'undefined' ? '' : window.location.pathname;
}

/**
 * Record that *we* are about to open the chat, so the resulting open is attributed to the
 * in-page pill rather than the floating launcher.
 *
 * Called by components/AskDocsBotButton.vue. The alternative — having ./DocsBot.js report its
 * own calls — would make the two helpers import each other.
 *
 * @param {string} trigger e.g. 'pill'
 */
export function noteDocsBotOpenTrigger(trigger) {
    lastTrigger = trigger;
    lastTriggerAt = Date.now();
}

/** Consume the noted trigger, falling back to the launcher once it has gone stale. */
function takeTrigger() {
    const fresh = lastTrigger && (Date.now() - lastTriggerAt) < TRIGGER_TTL;
    const trigger = fresh ? lastTrigger : 'launcher';
    lastTrigger = null;
    return trigger;
}

/**
 * A question our own pill put into the chat on the visitor's behalf.
 *
 * @param {string} question the seeded copy — ours, so safe to send
 * @param {string} [trigger]
 */
export function trackDocsBotSeededQuestion(question, trigger = 'pill') {
    lastAskedAt = Date.now();

    trackFullStoryEvent(EVENT_ASKED, {
        path: currentPath(),
        source: 'seeded',
        trigger,
        questionLength: question ? question.length : 0,
        question: question ? question.slice(0, MAX_SEEDED_LENGTH) : '',
    });
}

/** True for the things a person types into: the chat textarea, an input, or a rich-text div. */
function isTextEntry(node) {
    if (!node || node.nodeType !== 1) return false;

    const tag = node.tagName;

    if (tag === 'TEXTAREA') return true;
    if (tag === 'INPUT') {
        const type = (node.getAttribute('type') || 'text').toLowerCase();
        return ['text', 'search', 'email', 'url', 'tel', 'number', ''].includes(type);
    }

    return node.isContentEditable === true;
}

/** The field an event came from. Uses composedPath so a nested shadow tree still resolves. */
function textEntryFor(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    return path.find(isTextEntry) || null;
}

/** The anchor a click landed on, looking through wrapping spans/icons and nested trees. */
function anchorFor(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

    return path.find(node =>
        node && node.nodeType === 1 && node.tagName === 'A' && node.hasAttribute('href')
    ) || null;
}

function fieldLength(field) {
    if (!field) return 0;
    return (typeof field.value === 'string' ? field.value : field.textContent || '').length;
}

function recordAsked(field) {
    // Enter fires keydown, which then produces a `submit`; both describe one question.
    if (Date.now() - lastAskedAt < ASKED_DEDUPE_WINDOW) return;
    lastAskedAt = Date.now();

    trackFullStoryEvent(EVENT_ASKED, {
        path: currentPath(),
        source: 'typed',
        trigger: 'widget',
        // Deliberately the length only — never what they wrote. See the file header.
        questionLength: fieldLength(field),
    });
}

function onPanelChange(open) {
    if (open === panelOpen) return;
    panelOpen = open;

    if (!open) {
        // A fresh open is a fresh chance to record that they wrote something.
        typedThisOpen = false;
        return;
    }

    trackFullStoryEvent(EVENT_OPENED, {
        path: currentPath(),
        trigger: takeTrigger(),
    });
}

function onInput(event) {
    if (typedThisOpen) return;
    if (!textEntryFor(event)) return;

    typedThisOpen = true;
    trackFullStoryEvent(EVENT_TYPED, { path: currentPath() });
}

function onKeydown(event) {
    if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;

    const field = textEntryFor(event);
    if (!field || fieldLength(field) === 0) return;

    recordAsked(field);
}

function onSubmit(event) {
    // `submit` does not cross a shadow boundary, but this listener sits on the shadow root
    // itself, which is inside it — so the form's own submit still bubbles here.
    const field = event.target?.querySelector?.('textarea, input') || null;
    recordAsked(field);
}

function onClick(event) {
    const anchor = anchorFor(event);
    if (!anchor) return;

    const href = anchor.getAttribute('href') || '';
    let external = false;

    try {
        external = new URL(anchor.href, window.location.href).hostname !== window.location.hostname;
    } catch (e) {
        // Non-navigational href (mailto:, a bare fragment) — leave it as not-external.
    }

    trackFullStoryEvent(EVENT_LINK_CLICKED, {
        path: currentPath(),
        href,
        linkText: (anchor.textContent || '').trim().slice(0, 120),
        external,
    });
}

/**
 * Attach the delegated listeners to the widget's shadow root.
 *
 * Capture phase throughout: the widget stops propagation on some of its own handlers, and a
 * bubbling listener would then never see the event.
 */
function instrumentShadowRoot(shadowRoot) {
    shadowRoot.addEventListener('input', onInput, true);
    shadowRoot.addEventListener('keydown', onKeydown, true);
    shadowRoot.addEventListener('submit', onSubmit, true);
    shadowRoot.addEventListener('click', onClick, true);
}

/**
 * Start recording DocsBot usage to FullStory. Safe to call when neither is present.
 *
 * Called once per full page load from tenant/storefront/app.js. The widget and its shadow root
 * survive Inertia navigation, so these listeners are installed once and outlive every
 * client-side page change — re-attaching per visit would double-count.
 *
 * @returns {boolean} true if instrumentation was installed.
 */
export function initDocsBotAnalytics() {
    if (initialised || typeof window === 'undefined') return false;

    // `window.docsBotReady` is set only by the widget block in vue-layouts/app.blade.php, which
    // is gated on docsBotWidgetId(). Its presence is therefore an exact "the widget is on this
    // page" test, and a site without a bot pays nothing — not even a timer.
    if (typeof window.docsBotReady === 'undefined') return false;

    initialised = true;

    // Open/close is read from the shadow tree rather than the widget's API flag, so it stays
    // correct when the visitor closes the chat with the launcher or the Escape key.
    watchDocsBotPanel(onPanelChange);

    // WATCH_TIMEOUT, not the click default: these listeners are installed once per page load and
    // never retried, so a widget that mounts late (a slow `load` event, a cached chat.js) would
    // otherwise cost every Typed/Asked/Link event for the rest of the visit.
    whenDocsBotReady(WATCH_TIMEOUT).then(ready => {
        if (!ready) return;

        const shadowRoot = docsBotShadowRoot();
        if (shadowRoot) instrumentShadowRoot(shadowRoot);
    });

    return true;
}
